import { cn } from "@core/utils/cn";
import { forwardRef, type ComponentProps } from "react";
import Icon from "../Icon";

type ChipProps = Omit<ComponentProps<"div">, "children"> & {
  label: string;
  avatar?: string;
  onDismiss?: () => void;
  disabled?: boolean;
};

const Chip = forwardRef<HTMLDivElement, ChipProps>(
  (
    { label, avatar, onDismiss, disabled = false, className, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex items-center gap-3 px-1.5 py-1 border border-neutral-200 rounded bg-white text-neutral-950 text-xs transition-colors duration-200",
          !disabled && "hover:bg-surface-hover/50",
          disabled &&
            "bg-neutral-100 text-neutral-400 cursor-not-allowed pointer-events-none",
          className,
        )}
        {...props}
      >
        {/* Avatar + Label */}
        <div className="flex items-center gap-1">
          {avatar && (
            <span className="inline-flex items-center justify-center size-6 rounded-full bg-secondary-100 border border-neutral-200 text-secondary-900 text-[11px] font-medium shrink-0">
              {avatar}
            </span>
          )}
          <span className="font-normal">{label}</span>
        </div>

        {/* Dismiss icon */}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            disabled={disabled}
            className="flex items-center justify-center text-current disabled:cursor-not-allowed"
            aria-label="Remove"
          >
            <Icon name={"x-close"} strokeWidth={1.5} className="size-4" />
          </button>
        )}
      </div>
    );
  },
);

Chip.displayName = "Chip";

export default Chip;
